import { Dropdown, Icon, tailwindOverride } from '@getsynapse/design-system';
import { FC, ReactNode, MutableRefObject, useEffect, useRef } from 'react';
import { Option } from 'utils/customTypes';
import { StatusOption } from './StatusTableRowCell';
import { Options } from './Options';
import { useScrollLock, useDropdownPlacement } from './hooks';

interface StatusDropdownProps {
  value: string;
  customizedValue: (value: string) => ReactNode;
  selectedOption: StatusOption[];
  options: StatusOption[];
  onChange: (value: string) => void;
  toggleDropdownRef?: MutableRefObject<(() => void) | null>;
  triggerRef?: MutableRefObject<HTMLDivElement | null>;
  autoOpen?: boolean;
  saveTriggerRef?: MutableRefObject<(() => void) | null>;
}

export const StatusDropdown: FC<StatusDropdownProps> = ({
  value,
  customizedValue,
  selectedOption,
  options,
  onChange,
  toggleDropdownRef,
  triggerRef,
  autoOpen,
  saveTriggerRef,
}) => {
  const internalTriggerRef = useRef<HTMLDivElement | null>(null);
  const placement = useDropdownPlacement(internalTriggerRef, options.length);

  useScrollLock(true);

  const renderOption = (
    option: Option,
    selected: boolean,
    selectOption: () => void,
    { className, ...otherProps }: Record<string, any>
  ) => {
    const statusOption = option as StatusOption;
    const indexInOptions = options.findIndex(
      (o) => o.value === statusOption.value
    );

    const enhancedOtherProps = {
      ...otherProps,
      'data-status-option-index': indexInOptions.toString(),
    };

    return (
      <Options
        option={statusOption}
        selected={selected}
        selectOption={selectOption}
        otherProps={enhancedOtherProps}
        isHighlighted={false}
      />
    );
  };

  const moveFocusFromTrigger = (
    triggerEl: HTMLElement | null,
    direction: 'up' | 'down',
    selectedValue?: string
  ) => {
    if (!triggerEl) return;

    const root = triggerEl.closest(
      '[data-dropdown-root="true"]'
    ) as HTMLElement | null;
    if (!root) return;

    const enabledIndices = options
      .map((opt, idx) => (!opt.disabled ? idx : -1))
      .filter((idx) => idx !== -1);

    if (!enabledIndices.length) return;

    let targetIndexInOptions: number;

    if (!selectedValue) {
      targetIndexInOptions =
        direction === 'down'
          ? enabledIndices[0]
          : enabledIndices[enabledIndices.length - 1];
    } else {
      const selectedIdxInOptions = options.findIndex(
        (o) => o.value === selectedValue
      );
      const selectedIdxInEnabled = enabledIndices.indexOf(selectedIdxInOptions);

      if (selectedIdxInEnabled === -1) {
        targetIndexInOptions =
          direction === 'down'
            ? enabledIndices[0]
            : enabledIndices[enabledIndices.length - 1];
      } else {
        const nextIdxInEnabled =
          direction === 'down'
            ? selectedIdxInEnabled + 1
            : selectedIdxInEnabled - 1;

        if (nextIdxInEnabled < 0 || nextIdxInEnabled >= enabledIndices.length) {
          targetIndexInOptions = enabledIndices[selectedIdxInEnabled];
        } else {
          targetIndexInOptions = enabledIndices[nextIdxInEnabled];
        }
      }
    }

    const targetNode = root.querySelector<HTMLElement>(
      `[data-status-option-index="${targetIndexInOptions}"]`
    );
    if (targetNode) {
      targetNode.focus();
    }
  };

  useEffect(() => {
    if (autoOpen && toggleDropdownRef?.current) {
      requestAnimationFrame(() => {
        toggleDropdownRef.current?.();
        if (triggerRef?.current) {
          triggerRef.current.focus();
        }
      });
    }
  }, [autoOpen, toggleDropdownRef, triggerRef]);

  return (
    <div className='w-full' data-dropdown-root='true'>
      <Dropdown
        value={value}
        values={selectedOption}
        onChange={(option) => {
          onChange(option.value);
        }}
        options={options}
        renderOption={(
          option,
          selected,
          selectOption,
          { className, ...otherProps }
        ) => {
          return (
            <div className='min-w-40'>
              {renderOption(option, selected, selectOption, {
                className,
                ...otherProps,
              })}
            </div>
          );
        }}
        renderTrigger={({
          onToggleDropdown,
          selectedOptions,
          isDropdownExpanded,
          props,
        }) => {
          const triggerProps =
            (props as React.HTMLAttributes<HTMLDivElement> & {
              className?: string;
            }) || {};

          const {
            className: triggerClassName,
            onKeyDown: originalOnKeyDown,
            onClick: originalOnClick,
            ...restTriggerProps
          } = triggerProps;

          if (toggleDropdownRef) {
            toggleDropdownRef.current = onToggleDropdown;
          }

          const selectedValue = selectedOptions?.[0]?.value || value || '';

          const handleTriggerKeyDown = (
            event: React.KeyboardEvent<HTMLDivElement>
          ) => {
            const { key } = event;

            if (key === 'ArrowDown' || key === 'ArrowUp') {
              event.preventDefault();
              event.stopPropagation();
              moveFocusFromTrigger(
                event.currentTarget,
                key === 'ArrowDown' ? 'down' : 'up',
                selectedValue
              );
              return;
            }
          };

          const handleTriggerClick = (
            event: React.MouseEvent<HTMLDivElement>
          ) => {
            if (!isDropdownExpanded) {
              return originalOnClick?.(event);
            }

            originalOnClick?.(event);
            saveTriggerRef?.current?.();
          };

          const setRefs = (el: HTMLDivElement | null) => {
            internalTriggerRef.current = el;
            if (triggerRef) {
              triggerRef.current = el;
            }
          };

          return (
            <div
              {...restTriggerProps}
              ref={setRefs}
              onClick={handleTriggerClick}
              onKeyDown={handleTriggerKeyDown}
              className={tailwindOverride(
                'cursor-pointer flex items-center justify-between border rounded ml-1 p-2 outline-none focus:outline-none focus-visible:outline-none',
                triggerClassName
              )}
            >
              {selectedValue ? customizedValue(selectedValue) : ''}
              <Icon
                name={
                  isDropdownExpanded ? 'caret-up-outline' : 'caret-down-outline'
                }
              />
            </div>
          );
        }}
        className='w-full'
        popperDiffWidth={true}
        popperPlacement={placement}
      />
    </div>
  );
};
